import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import GlobalContext from "../../context/GlobalContext";
import DataTableComponent from "./DataTableComponent";
import {
  deactivateUser,
  getUserRoles,
  getUsers,
  reactivateUser,
  updateUserRole,
} from "../../api/UsersApi";
import defaultProfile from "../../assets/images/icon/default_profile.jpeg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarAlt,
  faEdit,
  faMoneyCheckAlt,
  faRotateLeft,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import QRDownloadButton from "../button/QrDownloadButton";
import swal from "../../services/swalConfig";

import { tr } from "../../i18n";
const TableUsers = ({ statusFilter = "Active" }) => {
  const { user, setLoading } = useContext(GlobalContext);
  const navigate = useNavigate();
  const [refreshKey, setRefreshKey] = useState(0);
  const [roles, setRoles] = useState([]);

  useEffect(() => {
    const loadRoles = async () => {
      const response = await getUserRoles();
      if (response?.status === 200 && Array.isArray(response.data)) {
        setRoles(response.data);
      }
    };

    loadRoles();
  }, []);

  const fetchData = useCallback(
    async ({ page, perPage, search }) => {
      if (!user?.workspace?.company_id) return { data: [], total: 0 };

      const response = await getUsers({
        companyId: user.workspace.company_id,
        page,
        perPage,
        search,
        status: statusFilter,
      });

      const numberedData = response.data.map((item, index) => ({
        ...item,
        role_id: item.role_id ? String(item.role_id) : "",
        role_name: item.role_name || "-",
        no: (page - 1) * perPage + index + 1,
      }));

      return { data: numberedData, total: response.total };
    },
    [user, refreshKey, statusFilter],
  );

  const handleEdit = (id) =>
    navigate("/users/edit", { state: { personnel_id: id } });
  const handleSchedule = (id) =>
    navigate("/users/edit/schedule", { state: { personnel_id: id } });
  const handlePayroll = (id) => navigate("/payroll/form?personnel=" + id);

  const roleOptions = useMemo(
    () => roles.map((r) => ({ value: String(r.role_id), label: r.role_name })),
    [roles],
  );

  const getStatusBadgeClass = (status) => {
    const normalized = (status || "").toLowerCase();
    if (normalized === "active") return "bg-success";
    if (normalized === "pending") return "bg-warning text-dark";
    return "bg-secondary";
  };

  const getDepartmentText = (row) =>
    row?.department_name || row?.department || row?.internal_department || "";

  const getSupervisorText = (row) =>
    row?.supervisor_name || row?.supervisor || row?.spv || "";

  const handleUpdateRole = async (row, selectedRole) => {
    if (!selectedRole || String(selectedRole) === String(row.role_id)) {
      return;
    }

    const selectedRoleName =
      roleOptions.find((opt) => String(opt.value) === String(selectedRole))
        ?.label || "selected role";

    const confirm = await swal.fire({
      title: tr("users:change_role"),
      text: tr("users:are_you_sure_you_want_to", { name: row.name, selectedRoleName }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: tr("users:yes_change_role"),
      cancelButtonText: tr("common:actions.cancel"),
    });

    if (!confirm.isConfirmed) {
      setRefreshKey((prev) => prev + 1);
      return;
    }

    setLoading(true);
    try {
      const response = await updateUserRole({
        company_id: user.workspace.company_id,
        personnel_id: row.personnel_id,
        role_id: selectedRole,
      });

      if (response?.status === 200) {
        swal.fire({
          title: tr("shared:success_2"),
          text: response.message,
          icon: "success",
          showCancelButton: false,
        });
        setRefreshKey((prev) => prev + 1);
        return;
      }

      swal.fire({
        title: tr("users:failed"),
        text: response?.message || tr("users:failed_to_update_role"),
        icon: "error",
        showCancelButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivate = async (row) => {
    const confirm = await swal.fire({
      title: tr("users:deactivate_user_2"),
      text: tr("users:user_value_will_lose_access_to", { value: row.name || row.user_email }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: tr("users:deactivate"),
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await deactivateUser({
        company_id: user.workspace.company_id,
        personnel_id: row.personnel_id,
      });

      if (response?.status === 200) {
        swal.fire({
          title: tr("shared:success_2"),
          text: response.message,
          icon: "success",
          showCancelButton: false,
        });
        setRefreshKey((prev) => prev + 1);
        return;
      }

      swal.fire({
        title: tr("users:failed"),
        text: response?.message || tr("users:failed_to_deactivate_user"),
        icon: "error",
        showCancelButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleReactivate = async (row) => {
    const confirm = await swal.fire({
      title: tr("users:reactivate_user_2"),
      text: tr("users:user_value_will_regain_access_to", { value: row.name || row.user_email }),
      icon: "question",
      showCancelButton: true,
      confirmButtonText: tr("users:reactivate"),
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setLoading(true);
    try {
      const response = await reactivateUser({
        company_id: user.workspace.company_id,
        personnel_id: row.personnel_id,
      });

      if (response?.status === 200) {
        swal.fire({
          title: tr("shared:success_2"),
          text: response.message,
          icon: "success",
          showCancelButton: false,
        });
        setRefreshKey((prev) => prev + 1);
        return;
      }

      swal.fire({
        title: tr("users:failed"),
        text: response?.message || tr("users:failed_to_reactivate_user"),
        icon: "error",
        showCancelButton: false,
      });
    } finally {
      setLoading(false);
    }
  };

  const commonCellStyle = { fontSize: "13px" };

  const columns = [
    {
      name: tr("shared:no"),
      selector: (row) => row.no,
      width: "60px",
      center: true,
      style: commonCellStyle,
    },
    {
      name: tr("users:user"),
      cell: (row) => {
        const departmentText = getDepartmentText(row);
        const supervisorText = getSupervisorText(row);

        return (
          <div className="table-users-user-cell d-flex align-items-center gap-2 py-1">
            <img
              src={row.picture || defaultProfile}
              onError={(e) => (e.target.src = defaultProfile)}
              alt={tr("users:avatar")}
              style={{
                width: 34,
                height: 34,
                borderRadius: "50%",
                objectFit: "cover",
              }}
            />
            <div className="table-users-user-text">
              <div style={{ fontSize: "13px", fontWeight: 600 }}>
                {row.name}
              </div>
              <div
                className="table-users-user-email"
                style={{ fontSize: "12px", color: "#666" }}
              >
                {row.user_email || "-"}
              </div>
              <div
                className="table-users-user-id"
                style={{ fontSize: "11px", color: "#8a8a8a" }}
              >
                {tr("users:id_2")}{" "} {row.internal_id || "-"}
              </div>
              <div style={{ fontSize: "11px", color: "#6b7280" }}>
                {tr("users:department")}{" "} {departmentText || "-"}
              </div>
            </div>
          </div>
        );
      },
      width: "250px",
      sortable: true,
      style: commonCellStyle,
    },
    {
      name: tr("users:supervisor"),
      cell: (row) => getSupervisorText(row) || "-",
      width: "170px",
      style: commonCellStyle,
    },
    {
      name: tr("shared:status"),
      cell: (row) => (
        <span className={`badge ${getStatusBadgeClass(row.status)}`}>
          {row.status ? tr(`shared:status_${row.status.toLowerCase()}`, { defaultValue: row.status }) : tr("users:unknown")}
        </span>
      ),
      width: "120px",
      center: true,
      style: commonCellStyle,
    },
    {
      name: tr("users:role"),
      cell: (row) => {
        const currentRole = row.role_id || "";

        return (
          <div
            className="d-flex align-items-center gap-1"
            style={{ minWidth: "170px" }}
          >
            <select
              className="form-select form-select-sm"
              value={currentRole}
              onChange={(e) => handleUpdateRole(row, e.target.value)}
              style={{ minWidth: "150px", fontSize: "12px" }}
            >
              <option value="">{tr("users:select_role")}</option>
              {roleOptions.map((role) => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
        );
      },
      width: "190px",
      ignoreRowClick: true,
    },
    {
      name: tr("shared:actions"),
      cell: (row) => (
        <div className="table-users-actions d-flex gap-1 align-items-center py-2">
          <QRDownloadButton
            qrUrl={row.qr_code}
            fileName={`${row.name.toLowerCase().replace(/\s+/g, "_")}_qr.png`}
            compact={true}
            className="table-users-action-btn table-users-action-btn-qr"
          />

          <button
            onClick={() => handleSchedule(row.personnel_id)}
            className="btn btn-sm table-users-action-btn table-users-action-btn-schedule p-0"
            title={tr("users:edit_schedule_2")}
          >
            <FontAwesomeIcon icon={faCalendarAlt} />
          </button>

          <button
            onClick={() => handlePayroll(row.personnel_id)}
            className="btn btn-sm table-users-action-btn table-users-action-btn-payroll p-0"
            title={tr("users:edit_payroll")}
          >
            <FontAwesomeIcon icon={faMoneyCheckAlt} />
          </button>

          {/* Tombol Utama */}
          <button
            onClick={() => handleEdit(row.personnel_id)}
            className="btn btn-sm table-users-action-btn table-users-action-btn-edit p-0"
            title={tr("users:edit_user_info")}
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>

          {(row.status || "").toLowerCase() === "inactive" ? (
            <button
              onClick={() => handleReactivate(row)}
              className="btn btn-sm table-users-action-btn table-users-action-btn-reactivate p-0"
              title={tr("users:reactivate_user")}
            >
              <FontAwesomeIcon icon={faRotateLeft} />
            </button>
          ) : (
            <button
              onClick={() => handleDeactivate(row)}
              className="btn btn-sm table-users-action-btn table-users-action-btn-deactivate p-0"
              title={tr("users:deactivate_user")}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          )}
        </div>
      ),
      width: "150px",
      ignoreRowClick: true,
    },
  ];

  return (
    <div>
      <DataTableComponent columns={columns} fetchData={fetchData} />
    </div>
  );
};

export default TableUsers;
